import React, { FC, useState } from 'react';
import { BhiDrawer } from 'src/UIKit';
import { EditContactDrawerController } from '../../controllers/EditContactDrawerController';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { Box, CircularProgress, Divider, Stack, Theme, useMediaQuery } from '@mui/material';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useSelector } from 'react-redux';
import {
  useMarkContactAsValidDialog,
  useSpamContactDialog,
} from 'src/modules/inbox/components';
import { BhiAccordion } from 'src/components/BhiAccordion';
import { FormView } from 'src/components/FormPart/FormView';
import { buildContactInfoFormData, buildContactCustomFieldsFormData } from '../../helpers/builders';
import { AssignUserForm } from './AssignUserForm';
import { EditContactGroupsForm } from './EditContactGroupsForm';
import { EditContactInfoForm } from './EditContactInfoForm';
import { EditContactMetadataForm } from './EditContactMetadataForm';
import useMarkContactAsOptOutDialog from './MarkContactAsOptOutDialog';

interface EditContactDrawerProps extends EditContactDrawerController {}

export const EditContactDrawer: FC<EditContactDrawerProps> = props => {
  const {
    isOpen,
    close,
    contactData,
    groups,
    users,
    contactGroups,
    submitContactInfo,
    submitAssignUser,
    submitChangeContactGroups,
    submitContactMetadata,
    refreshContacts,
  } = props;

  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const { SpamContactDialog, SpamButton } = useSpamContactDialog();
  const { MarkContactAsValidDialog, MarkContactAsValidButton } = useMarkContactAsValidDialog();
  const { MarkContactAsOptOutDialog, MarkContactAsOptOutButton } = useMarkContactAsOptOutDialog();
  const [isContactFormDirty, setIsContactFormDirty] = useState<boolean>(false);
  const [isGroupsFormDirty, setIsGroupsFormDirty] = useState<boolean>(false);
  const [isMetadataFormDirty, setIsMetadataFormDirty] = useState<boolean>(false);

  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const isFormsDirty = () => {
    return isContactFormDirty || isGroupsFormDirty || isMetadataFormDirty;
  };

  const cleanForms = () => {
    setIsContactFormDirty(false);
    setIsGroupsFormDirty(false);
    setIsMetadataFormDirty(false);
  };

  const handleClose = () => {
    if (!isFormsDirty()) {
      close();
    }
    if (isFormsDirty()) {
      openDialog({
        title: `You have unsaved changes!`,
        content: 'Do you want to continue without saving?',
        onConfirm: () => {
          cleanForms();
          close();
        },
      });
    }
  };

  return (
    <BhiDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Contact"
      titleActions={
        contactData && (
          <AssignUserForm
            currentCompanyId={currentCompanyId}
            contactId={contactData.id}
            assignedUserId={contactData.assignedTo}
            users={users || []}
            submitAssignUser={submitAssignUser}
          />
        )
      }
    >
      {contactData && (
        <>
          <SpamContactDialog />
          <MarkContactAsValidDialog />
          <MarkContactAsOptOutDialog />
          <ConfirmationDialog />
          <BhiAccordion flat sx={{ mb: 1.25 }} defaultExpanded title="Contact Info">
            <FormView
              setFormIsDirty={(isFormDirty: boolean) => {
                setIsContactFormDirty(isFormDirty);
              }}
              formData={buildContactInfoFormData(contactData)}
              component={EditContactInfoForm}
              onSubmit={(data, _, form) => submitContactInfo(data, form)}
            />
          </BhiAccordion>
          <BhiAccordion flat sx={{ mb: 1.25 }} title="Groups">
            {!contactGroups && <CircularProgress />}
            {contactGroups && (
              <EditContactGroupsForm
                currentCompanyId={currentCompanyId}
                contactId={contactData.id}
                contactGroupIds={contactGroups.map(c => c.id) || []}
                groups={groups || []}
                setIsFormDirty={setIsGroupsFormDirty}
                isFormDirty={isGroupsFormDirty}
                submitContactGroups={submitChangeContactGroups}
              />
            )}
          </BhiAccordion>
          {contactData.metadata.length > 0 && (
            <BhiAccordion flat title="Additional Contact Details">
              <FormView
                setFormIsDirty={(isFormDirty: boolean) => {
                  setIsMetadataFormDirty(isFormDirty);
                }}
                formData={buildContactCustomFieldsFormData(contactData)}
                component={EditContactMetadataForm}
                onSubmit={(data, _, form) => submitContactMetadata(data, form)}
              />
            </BhiAccordion>
          )}
          <Divider flexItem sx={{ height: 3, my: 2.5 }} />
          <Box mb={isMobile ? 6.25 : 0}>
            <Stack direction="row-reverse">
              {contactData.validStatus === 'valid' && (
                <SpamButton
                  fullWidth
                  selectedCompanyId={currentCompanyId!}
                  contactId={contactData.id}
                  isSpam={!!contactData.isSpam?.value}
                  nextAction={() => {
                    close();
                    refreshContacts();
                  }}
                />
              )}
              {contactData.validStatus === 'invalid' && (
                <MarkContactAsValidButton
                  selectedCompanyId={currentCompanyId!}
                  contact={contactData}
                  fullWidth
                  nextAction={() => {
                    close();
                    refreshContacts();
                  }}
                />
              )}
            </Stack>
            {!contactData.optOutDate && (
              <Box mt={1.25}>
                <MarkContactAsOptOutButton
                  selectedCompanyId={currentCompanyId!}
                  contactId={contactData.id}
                  fullWidth
                  nextAction={() => {
                    close();
                    refreshContacts();
                  }}
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </BhiDrawer>
  );
};
